class HanroProductSwatches extends HTMLElement {
  #originalHandle = null;
  #originalSlideStates = [];

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);

    // Snapshot original state for restoring when clicking back to default product
    const activeSwatch = this.querySelector('[data-swatch].active');
    if (activeSwatch) this.#originalHandle = activeSwatch.dataset.productHandle;

    const card = this.closest('product-card');
    if (card) {
      // Capture each slide's image + visibility so the original product's full
      // gallery can be restored after a sibling swap.
      card.querySelectorAll('slideshow-slide').forEach((slide) => {
        const img = slide.querySelector('.product-media__image') || slide.querySelector('img');
        this.#originalSlideStates.push({
          slide,
          img,
          src: img?.getAttribute('src') || '',
          srcset: img?.getAttribute('srcset') || '',
          hidden: slide.hidden,
        });
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const swatch = event.target.closest('[data-swatch]');
    if (!swatch || swatch.classList.contains('active')) return;

    const card = this.closest('product-card');
    if (!card) return;

    // In navigate mode (swatches-only template), let the link work naturally
    if (this.dataset.mode === 'navigate') {
      window.location.href = swatch.dataset.productUrl;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Update active state
    this.querySelectorAll('[data-swatch]').forEach((s) => s.classList.remove('active'));
    swatch.classList.add('active');

    this.#updateImage(card, swatch);
    this.#updateLinks(card, swatch);
    this.#updatePrice(card, swatch);
    this.#updateBadges(card, swatch);
  };

  #updateImage(card, swatch) {
    const scroller = card.querySelector('slideshow-slides[ref="scroller"]');
    if (!scroller) return;
    const arrows = card.querySelector('slideshow-arrows');

    const isOriginal = swatch.dataset.productHandle === this.#originalHandle;

    if (isOriginal) {
      // Drop any slides we added for a larger colorway, then restore the original
      // product's full gallery: each original slide's image and visibility.
      scroller.querySelectorAll('slideshow-slide[data-hanro-dynamic-slide]').forEach((slide) => slide.remove());
      this.#originalSlideStates.forEach(({ slide, img, src, srcset, hidden }) => {
        if (img) {
          if (src) img.src = src;
          img.srcset = srcset;
        }
        slide.hidden = hidden;
      });
      if (arrows) { arrows.hidden = false; arrows.style.display = ''; }
      this.#resetSlideshow(card);
      this.#refreshIndicators(card);
      return;
    }

    // Sibling selected: swap in that color's FULL image gallery so the card carousel
    // keeps working. The sibling's media is resolved lazily on click (HAN-398) —
    // resolving every sibling's media at render time exhausts Shopify's per-request
    // connected-resource budget and drops swatches from later cards. Cached per swatch.
    this.#fetchSwatchMedia(swatch).then((images) => {
      // Only apply if this swatch is still the active selection.
      if (!swatch.classList.contains('active')) return;

      // Fall back to the swatch's single featured image if the media fetch failed.
      let gallery = images;
      if (!gallery.length && swatch.dataset.srcset) {
        gallery = [{ src: swatch.dataset.srcset.split(' ')[0], srcset: swatch.dataset.srcset }];
      }
      if (!gallery.length) return;

      this.#applyGallery(card, scroller, arrows, gallery);
      this.#resetSlideshow(card);
      this.#refreshIndicators(card);
    });
  }

  // Tell the card's image indicators to rebuild after the slide set changed.
  #refreshIndicators(card) {
    const indicators = card.querySelector('hanro-image-indicators');
    if (indicators && typeof indicators.refresh === 'function') indicators.refresh();
  }

  // Show the given images in the card gallery. The slide pool is grown (by cloning)
  // or shrunk so it holds exactly one slide per image — a colorway with more images
  // than the default product is NOT capped to the default's slide count.
  #applyGallery(card, scroller, arrows, images) {
    const productId = card.getAttribute('data-product-id') || 'sibling';
    const slides = [...scroller.querySelectorAll(':scope > slideshow-slide')];

    // Grow: clone the first slide for each missing slot.
    while (slides.length < images.length) {
      const template = slides[0];
      if (!template) break;
      const clone = template.cloneNode(true);
      clone.dataset.hanroDynamicSlide = 'true';
      clone.setAttribute('slide-id', `${productId}-dyn-${slides.length}`);
      clone.style.setProperty('--slideshow-timeline', `--slide-${slides.length}`);
      clone.removeAttribute('hidden');
      scroller.appendChild(clone);
      slides.push(clone);
    }

    slides.forEach((slide, i) => {
      const img = slide.querySelector('.product-media__image') || slide.querySelector('img');
      if (i < images.length) {
        if (img) {
          img.srcset = images[i].srcset;
          img.src = images[i].src;
        }
        slide.hidden = false;
      } else if (slide.dataset.hanroDynamicSlide) {
        // Surplus clone from a previous (larger) colorway — remove it.
        slide.remove();
      } else {
        // Surplus original slide — hide it.
        slide.hidden = true;
      }
    });

    if (arrows) {
      const show = images.length > 1;
      arrows.hidden = !show;
      arrows.style.display = show ? '' : 'none';
    }
  }

  // Reset the slideshow back to its first slide (no animation).
  #resetSlideshow(card) {
    const slideshow = card.querySelector('slideshow-component');
    if (!slideshow?.select) return;
    const firstSlide = card.querySelector('slideshow-slide');
    const slideId = firstSlide?.getAttribute('slide-id');
    if (slideId) slideshow.select({ id: slideId }, undefined, { animate: false });
  }

  // Fetch a sibling product's image list and build width-based srcsets client-side.
  // Returns [] on failure (caller keeps the current images). Cached per swatch.
  #fetchSwatchMedia = async (swatch) => {
    if (swatch._media) return swatch._media;
    if (swatch._mediaPromise) return swatch._mediaPromise;

    swatch._mediaPromise = (async () => {
      try {
        const handle = swatch.dataset.productHandle;
        if (!handle) return [];
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}products/${handle}.js`);
        if (!res.ok) return [];
        const data = await res.json();
        // Cap at 8 to keep the card carousel reasonable while still showing a
        // colorway's full set even when it has more images than the default product.
        const urls = (data.images || []).slice(0, 8);
        const images = urls.map((base) => {
          const sep = base.includes('?') ? '&' : '?';
          const srcset = [375, 770, 1100].map((w) => `${base}${sep}width=${w} ${w}w`).join(', ');
          return { src: `${base}${sep}width=770`, srcset };
        });
        swatch._media = images;
        return images;
      } catch {
        return [];
      }
    })();

    return swatch._mediaPromise;
  };

  #updateLinks(card, swatch) {
    const url = swatch.dataset.productUrl;
    const title = swatch.dataset.productTitle || '';
    if (!url) return;

    // Update all card links: main card link, gallery link, title link
    card.querySelectorAll('a.product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]').forEach((link) => {
      link.href = url;
      if (link.title !== undefined) link.title = title;
    });

    // Update the visually-hidden text in the main card link
    const hiddenText = card.querySelector('a.product-card__link .visually-hidden');
    if (hiddenText) hiddenText.textContent = title;

    // Update the visible product title text
    const titleEl = card.querySelector('a[ref="productTitleLink"] p');
    if (titleEl) titleEl.textContent = title;
  }

  #updatePrice(card, swatch) {
    const priceContainer = card.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const price = parseInt(swatch.dataset.productPrice, 10);
    const compareAt = parseInt(swatch.dataset.compareAtPrice, 10);
    if (isNaN(price)) return;

    const formatted = this.#formatMoney(price);

    if (compareAt && compareAt > price) {
      const formattedCompare = this.#formatMoney(compareAt);
      const savePercent = Math.round((1 - price / compareAt) * 100);
      priceContainer.innerHTML =
        '<span role="group"><span class="price">' + formatted + '</span></span>' +
        '<span role="group"><span class="compare-at-price">' + formattedCompare + '</span></span>' +
        (savePercent > 0 ? '<span class="price-save-percent">Save ' + savePercent + '%</span>' : '');
    } else {
      priceContainer.innerHTML =
        '<span role="group"><span class="price">' + formatted + '</span></span>';
    }
  }

  #updateBadges(card, swatch) {
    const container = card.querySelector('.product-badges');
    if (!container) return;

    const existingBadge = container.querySelector('.product-badges__badge');
    if (existingBadge) existingBadge.remove();
  }

  #formatMoney(cents) {
    const format = window.Shopify?.money_format || window.theme?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    let result = format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);

    if (window.Theme?.settings?.removeTrailingZeros) {
      result = result.replace(/\.00(?=\s|$)/, '');
    }

    return result;
  }
}

customElements.define('hanro-product-swatches', HanroProductSwatches);
